import React from "react";
import { Transaction } from "../../types";

interface TodayTransactionsProps {
  transactions: Transaction[];
  todayStats: {
    todayCredits: number;
    todayDebits: number;
    todayCount: number;
  };
}

const TodayTransactions: React.FC<TodayTransactionsProps> = ({
  transactions,
  todayStats,
}) => {
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount: number, type: "credit" | "debit") => {
    const formattedAmount = amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
    });
    return type === "credit" ? `+₹${formattedAmount}` : `-₹${formattedAmount}`;
  };

  const getAccountName = (account: any) => {
    if (typeof account === "string") return "Account";
    return account?.bankName || "Unknown Bank";
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-500 opacity-10 rounded-bl-full"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Today's Activity
          </h3>
          <div className="text-sm text-gray-500">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </div>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
            <div className="text-lg font-bold text-green-600">
              +₹
              {todayStats.todayCredits.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </div>
            <div className="text-xs text-green-600 font-medium">Credits</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
            <div className="text-lg font-bold text-red-600">
              -₹
              {todayStats.todayDebits.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </div>
            <div className="text-xs text-red-600 font-medium">Debits</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-lg font-bold text-blue-600">
              {todayStats.todayCount}
            </div>
            <div className="text-xs text-blue-600 font-medium">
              Transactions
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Recent Transactions
          </h4>
          {transactions.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {transactions.map((transaction, index) => (
                <div
                  key={transaction._id || index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          transaction.type === "credit"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      ></div>
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {transaction.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{getAccountName(transaction.account)}</span>
                      <span>•</span>
                      <span>{formatTime(transaction.date)}</span>
                    </div>
                  </div>
                  <div
                    className={`text-sm font-semibold ${
                      transaction.type === "credit"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {formatAmount(transaction.amount, transaction.type)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
              </div>
              <p className="text-sm text-gray-500">No transactions today</p>
              <p className="text-xs text-gray-400 mt-1">
                Start by adding a transaction
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodayTransactions;
